import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Navigate, useParams } from "react-router-dom";

import Button from "@mui/material/Button";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";

import ScrollableTabs from "@meetyoo/components/src/components/Tabs/ScrollableTabs";

import { PM_CONFERENCES_PATH } from "../../../constants/router";

import PMConference from "../PMConference";
import PMBillingData from "../PMBillingData";
import PMHostAndGuest from "../PMHostAndGuest";
import PMGeneralOptions from "../PMGeneralOptions";
import PMPrepaidAndAlert from "../PMPrepaidAndAlert";
import PMEntry from "../PMEntry";
import PMInternalInfo from "../PMInternalInfo";

import { difference } from "../../../utils/objectMethods";

import { requestConferences, updateConference, requestParams, updatePin } from "../../../actions/passcodeManager";

const customStyle = theme => ({
  Box: {
    padding: theme.spacing(0),
  },
  appBar: {
    boxShadow: "none",
  },
});

const confRestRepInit = {
  owner: "",
  product: "",
  hostPasscode: "",
  guestPasscode: "",
  unattendedDialoutAllowed: false,
  unattendedDialoutGuestAllowed: false,
};

export const PMTabsComponent = ({
  conferences,
  selectValues,
  filter,
  username,
  translate,
  getConferencesHanlder,
  updateConferenceHandler,
  requestSelectValues,
  errorMessage,
  updatePinHandler,
}) => {
  const [confOriginal, setConfOriginal] = useState(confRestRepInit);
  const [confRestRep, setConfRestRep] = useState(confRestRepInit);
  const [updateRequest, setUpdateRequest] = useState(false);
  const { id: idParam } = useParams();
  const id = Number(idParam);

  useEffect(() => {
    const conf = conferences.filter(c => c.conferenceId === id)[0] || confRestRepInit;
    if (conferences.length === 0) getConferencesHanlder(filter);
    else if (conf.conferenceId === id && confRestRep.guestPasscode !== conf.guestPasscode) {
      setConfRestRep({
        ...confRestRep,
        guestPasscode: conf.guestPasscode,
        hostPasscode: conf.hostPasscode,
      });
      setConfOriginal({
        ...confOriginal,
        guestPasscode: conf.guestPasscode,
        hostPasscode: conf.hostPasscode,
      });
    } else if (confOriginal.conferenceId !== id && confOriginal !== confRestRepInit) {
      setConfRestRep(conf);
      setConfOriginal(conf);
    }
    if (Object.keys(selectValues).length === 0) requestSelectValues();
  }, [
    conferences,
    getConferencesHanlder,
    confOriginal,
    confRestRep,
    id,
    requestSelectValues,
    selectValues,
    filter,
    setConfOriginal,
    setConfRestRep,
  ]);

  const buttonSx = theme => ({
    m: theme.spacing(2, 1, 1, 1),
    width: 100,
  });
  const spaceButtonsSx = { flex: 1, width: "10px" };
  const inLineSx = { width: "100%", display: "inline-flex" };
  const iconStartSx = { mr: 1, fontSize: 20 };
  const iconEndSx = { ml: 1, fontSize: 20 };

  return updateRequest || errorMessage !== "" ? (
    <Navigate to={PM_CONFERENCES_PATH} replace />
  ) : (
    [
      <Box sx={inLineSx} key="Tabs">
        {Object.keys(selectValues).length > 0 ? (
          <ScrollableTabs
            key={`pm_tabs_${confRestRep.conferenceId}`}
            customStyle={customStyle}
            tabs={[
              {
                title: translate.conferenceTab,
                component: (
                  <PMConference
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                    requestNewPin={updatePinHandler}
                  />
                ),
              },
              {
                title: translate.billingDataTab,
                component: (
                  <PMBillingData
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                  />
                ),
              },
              {
                title: translate.hostGuestTab,
                component: (
                  <PMHostAndGuest
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                  />
                ),
              },
              {
                title: translate.generelOptionsTab,
                component: (
                  <PMGeneralOptions
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                  />
                ),
              },
              {
                title: translate.alertPrepaidTab,
                component: (
                  <PMPrepaidAndAlert
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                  />
                ),
              },
              {
                title: translate.entryTab,
                component: (
                  <PMEntry
                    translate={translate}
                    confRestRep={confRestRep}
                    setConfRestRep={setConfRestRep}
                    selectValues={selectValues}
                  />
                ),
              },
              {
                title: translate.internalInfoTab,
                component: <PMInternalInfo translate={translate} conference={confRestRep} />,
              },
            ]}
          />
        ) : null}
      </Box>,
      <Box sx={inLineSx} key="Buttons">
        <Button
          key="pm_tabs_btn_save"
          variant="contained"
          size="small"
          color={confOriginal === confRestRep ? "inherit" : "primary"}
          sx={buttonSx}
          onClick={() => {
            if (Object.keys(difference(confRestRep, confOriginal)).length !== 0) {
              updateConferenceHandler(id, { ...difference(confRestRep, confOriginal), changedBy: username });
              setUpdateRequest(true);
            }
          }}
        >
          <SaveIcon sx={iconStartSx} />
          {translate.saveButton}
        </Button>
        <Box sx={spaceButtonsSx} />
        <Button
          onClick={() => {
            // TODO
          }}
          key="pm_tabs_btn_del"
          variant="contained"
          color="secondary"
          sx={buttonSx}
        >
          {translate.deleteButton}
          <DeleteIcon sx={iconEndSx} />
        </Button>
      </Box>,
    ]
  );
};

PMTabsComponent.propTypes = {
  translate: PropTypes.object.isRequired,
  conferences: PropTypes.array.isRequired,
  getConferencesHanlder: PropTypes.func.isRequired,
  updateConferenceHandler: PropTypes.func.isRequired,
  selectValues: PropTypes.object.isRequired,
  requestSelectValues: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  username: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  updatePinHandler: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  conferences: state.passcodeManager.conferences,
  selectValues: state.passcodeManager.selectValues,
  filter: state.passcodeManager.filter,
  username: state.oauth.username,
  errorMessage: state.passcodeManager.errorMessage,
  translate: state.ui.translate,
});

const mapDispatchToProps = dispatch => ({
  getConferencesHanlder: filter => dispatch(requestConferences(filter)),
  requestSelectValues: () => dispatch(requestParams()),
  updateConferenceHandler: (id, conference) => dispatch(updateConference(id, conference)),
  updatePinHandler: (id, type, confRestRep) => dispatch(updatePin(id, type, confRestRep)),
});

const PMTabs = connect(
  mapStateToProps,
  mapDispatchToProps
)(PMTabsComponent);

export default PMTabs;
